import type { TrainingProgress } from '@/lib/apiClient';

/**
 * Fold one topic's saved state into the cached progress list.
 *
 * ⚠️ WHY NOT JUST REPLACE THE CACHE WITH THE RESPONSE. `POST progress/:topicId`
 * answers with the reader's WHOLE progress list, and guided mode deliberately
 * lets someone click Next faster than the network — advancing is not blocked on
 * the write. So two saves can be in flight, and if the earlier response lands
 * last, its snapshot predates the later topic: replacing the cache wholesale
 * drops that tick, and the count and resume position visibly go backwards until
 * something refetches (review, #289).
 *
 * ⚠️ A "keep the newest row" merge is NOT available: TrainingProgress carries
 * topicId, completedAt, guideRevision and note — no updated_at — so there is
 * nothing to order two versions of the same row by. `completedAt` can't stand in
 * for it, because un-ticking sets it to null.
 *
 * What IS knowable is which topic the request was about. So take only that row
 * from the response and leave every other topic as the cache has it. A stale
 * response can then only speak for the topic it was sent about, and the
 * mutations are also serialized by scope so two writes to the SAME topic (tick,
 * then undo) can't overlap either.
 *
 * @param prev    the cached list, or undefined before the first fetch
 * @param rows    the full list the server just returned
 * @param topicId the topic this request was about — the only row we trust from it
 */
export function mergeProgressRow(
  prev: TrainingProgress[] | undefined,
  rows: TrainingProgress[],
  topicId: string,
): TrainingProgress[] {
  const saved = rows.find((r) => r.topicId === topicId);
  const base = prev ?? [];

  // The row is gone from the response: the topic was reset to nothing recorded
  // (no tick, no note), so drop it here too rather than leaving a ghost.
  if (!saved) return base.filter((r) => r.topicId !== topicId);

  const at = base.findIndex((r) => r.topicId === topicId);
  if (at === -1) return [...base, saved];

  const next = base.slice();
  next[at] = saved;
  return next;
}
